
/**
 * Import JavaScript Assets
 * @since 3.0.0
 */
import { Cookies } from "commonAssets/cookies";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { Header } from "commonAssets/components/Header";
import { Navigation } from "commonAssets/components/Navigation";
import { Footer } from "commonAssets/components/Footer";
import { ImportantDates } from "commonAssets/components/ImportantDates";
import { Page } from "commonAssets/pages/Page";
import { siteTitle, loadingGif } from "commonAssets/js/helpers";
import { I18n } from "commonAssets/i18n";
import {
    loading,
    siteInfoLang,
    userLanguage,
    setSiteInfo,
    setSiteInfoLang,
    setEmailInform,
    setPhoneAdmin,
    setEmailConfirm,
    setUserLanguage,
    setAppLocale,
    setI18n,
} from "commonAssets/js/constants";
import config from "./config";

/**
 * Internationalization (i18n) Handling
 *
 * Sets the langauges available for the distribution.
 *
 * Examples:
 * i18n.languages returns languages in an array
 * i18n.userLanguage returns current languages
 * i18n.getTranslation('string') returns predefined string
 */
const i18n = I18n.create(["en", "es"], {});
setI18n(() => i18n);

/**
 * Cookies
 * @returns {void}
 */
const setCookies = new Cookies();
setCookies.init();

/**
 * Set the user's language using the value from `setCookies.language()`.
 * @param {Function} setCookiesLanguage - The function that returns the user's language.
 */
setUserLanguage(() => setCookies.language());

/**
 * Set the application locale using the value from `setCookies.locale()`.
 * @param {Function} setCookiesLocale - The function that returns the application locale.
 */
setAppLocale(() => setCookies.locale());

/**
 * Loading GIF
 */
loadingGif(loading, "show");

/**
 * Initialize the analytics by loading the Google Tag Manager script and
 * configuring the dataLayer and gtag.
 */
export function analytics() {
    $.getScript(
        "https://www.googletagmanager.com/gtag/js?id=G-LGF2TN8VN9",
        () => {
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                dataLayer.push(arguments);
            }
            gtag("js", new Date());

            gtag("config", "G-WKXC7VDFFT"); // swatjs
            gtag("config", "G-LGF2TN8VN9"); // general

            // Add additional `gtag`s here.
            gtag("config", "G-XX937V25XP");
        }
    );

    // Add custom pixels/analytics code here.
}

/**
 * Calls the appropriate function to display page content based on the given domainSlug.
 * If domainSlug is empty, the 'home' case will be executed.
 *
 * @param {Array} sitePages - An array containing the website sitePages' information.
 * @param {string} domainSlug - The unique identifier (slug) of the page to be displayed.
 * @param {} importantDates -
 *
 * @example
 * // Assuming sitePages array and domainSlug are defined and valid
 * getPage(sitePages, domainSlug, importantDates);
 */
export function getPage(sitePages, domainSlug, importantDates) {
    loadingGif(loading, "show");
    // Set domainSlug to 'home' if empty
    domainSlug === "" ? siteTitle(sitePages) : siteTitle(sitePages, domainSlug);

    // Misc handling.
    domainSlug = domainSlug || "home";
    const page = new Page(sitePages, domainSlug, importantDates);

    switch (domainSlug) {
        case "home":
            page.home();
            analytics();
            moveTableBelowSubHeader2();
            break;

        case "demo":
            page.default();
            analytics();
            break;

        // case 'submit-claim':
        //     customInitGuard(data)
        //     const submit_claim = new SubmitClaim(appClaimForm, userLanguage, customInitClaim, customProcessClaim, customLoadMember);
        //     submit_claim.form();
        //     analytics();
        //     break;

        // case 'opt-out':
        //     const opt_out = new OptOut(appClaimForm, userLanguage, i18n, false, false);
        //     opt_out.form();
        //     analytics();
        //     break;

        case "important-documents":
            page.important_documents();
            analytics();
            break;

        case "faqs":
            page.faqs();
            analytics();
            break;

        case "contact-us":
            page.contact();
            analytics();
            break;

        case "en":
            window.location.href = "/?language=en";
            break;

        case "es":
            window.location.href = "/?language=es";
            break;

        default:
            page.error();
            analytics();
    }
    removeImportantDates()
}

function removeImportantDates() {
    const importantDates = document.querySelector("#ImportantDates");
    if (importantDates) {
        importantDates.remove();
    }
}

function moveTableBelowSubHeader2() {
    const subHeader2 = document.querySelector("#pageSubHeader2_home");
    if (subHeader2) {
        const table = document.querySelector("#optionsTABLE_home");
        if (table) {
            subHeader2.insertAdjacentElement("afterend", table);
        }
    }
}

/**
 * Fetches domain API data and updates the website with the retrieved data.
 * Updates site title, site name, case number, court name, and builds navigation
 * based on the fetched data. Also adds event listener for hash changes to
 * update page content accordingly.
 *
 * @example
 * // Assuming that appDocketAPI and appLang are defined and valid
 * fetch(appDocketAPI)
 * .then(response => response.json())
 * .then(data => { ... })
 * .catch(error => console.error(error));
 */
const token = setCookies.getCookie("_ag_purl")
    ? "&uuid=" + setCookies.getCookie("_ag_purl")
    : "";
let getTheApiEndpoint;
if (
    window.location.href.includes("local") ||
    window.location.href.includes("angeiontest")
) {
    getTheApiEndpoint = `https://os46zn9hmk.execute-api.us-east-1.amazonaws.com/dev/websiteV2?domain=${config.appDocket}&active=true`;
} else {
    // getTheApiEndpoint = appDocketAPI;
    getTheApiEndpoint = `https://uae4khqoag.execute-api.us-east-1.amazonaws.com/prod/websiteV2?domain=${config.appDocket}&active=true`;
}
fetch(getTheApiEndpoint + token)
    .then((response) => {
        if (response.status > 299) {
            // Hide loading gif
            loadingGif(loading, "hide", 1000);
            // Display error message
            const error = new ErrorHandler();
            error.display(response);
        }
        return response.json();
    })
    .then((data) => {
        setSiteInfoLang(() => data.findResult.lang[userLanguage]);
        setSiteInfo(() => data.findResult);
        setEmailInform(() => data.findResult.email_info);
        setPhoneAdmin(() => data.findResult.phone);
        setEmailConfirm(() => data.findResult.email_conf);

        document.title = siteInfoLang.SiteTitle;

        // Returns the first value after FQDN. Example: fqdn.com/THIS/foo/bar
        const domainSlug = window.location.pathname.split("/")[1];

        let sitePages = siteInfoLang.Pages;
        sitePages = sitePages
            .filter(
                (page) =>
                    page.PageTitle != null && page.PageTitle != "Submit Claim"
            )
            .map((page) => {
                if (page.PageTitle === "FAQs") return page;

                return {
                    ...page,
                    PageTitle: page.PageTitle.toUpperCase(),
                };
            });

        const header = new Header(
            siteInfoLang.SiteName,
            siteInfoLang.CaseNumber,
            siteInfoLang.CourtName
        );
        const navigation = new Navigation(sitePages, domainSlug, getPage);
        const footer = new Footer();
        const importantDates = new ImportantDates(data);

        const headerElem = document.querySelector("header");
        headerElem.classList.add("py-3");
        headerElem.appendChild(header.createHeader());

        navigation.pathChange(sitePages, domainSlug, importantDates);

        document
            .querySelector("header")
            .insertAdjacentHTML("afterend", navigation.element.outerHTML);
        document.querySelector("footer").appendChild(footer.element);

        getPage(sitePages, domainSlug, importantDates, data);
    })
    .catch((error) => console.error(error));
